import React, { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Image, Button } from "@heroui/react";
import {
  Menu,
  X,
  Home,
  ShoppingBag,
  BarChart3,
  ArrowUpDown,
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { IMAGES } from "../localConstants/images";
import ConnectModal from "./ConnectModal";
import { useGetAccountInfo } from "../hooks/sdkDappHooks";
import makeBlockie from "ethereum-blockies-base64";
import { useBuyA1XModal } from "../contexts/BuyA1XModalContext";

export const Navbar = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  const { address } = useGetAccountInfo();
  const { openModal } = useBuyA1XModal();

  const isActive = (path: string) => location.pathname === path;

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const handleBuyA1X = () => {
    openModal();
    setIsMobileMenuOpen(false);
  };

  const avatarUrl = address ? makeBlockie(address) : null;

  const menuItems = [
    { path: "/", label: "Dashboard", icon: Home },
    { path: "/marketplace", label: "Marketplace", icon: ShoppingBag },
    { path: "/bridge", label: "Bridge", icon: ArrowUpDown },
    {
      path: "https://xexchange.com/staking/A1X-0d446d",
      label: "Staking",
      icon: BarChart3,
      external: true,
    },
  ];

  return (
    <>
      {/* Fixed Mobile Navbar */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-neutral-900 border-b border-neutral-700 md:hidden">
        <div className="flex items-center justify-between px-4 py-2">
          {/* Left: Logo */}
          <Link to="/" className="flex items-center flex-shrink-0">
            <Image src={IMAGES.AINEXUS} alt="AiNexus" className="h-7 w-auto" />
          </Link>

          {/* Right: Connect & Menu Button */}
          <div className="flex items-center gap-2 flex-shrink-0">
            {/* ConnectModal handles all user display logic */}
            <div className="scale-90 origin-right">
              <ConnectModal />
            </div>
            <Button
              isIconOnly
              variant="ghost"
              onPress={toggleMobileMenu}
              className="text-white hover:bg-neutral-800 flex-shrink-0"
              size="sm"
            >
              {isMobileMenuOpen ? <X size={18} /> : <Menu size={18} />}
            </Button>
          </div>
        </div>
      </nav>

      {/* Mobile Menu Overlay */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <>
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/70 z-40 md:hidden"
              onClick={toggleMobileMenu}
            />

            {/* Menu Content */}
            <motion.div
              initial={{ x: "100%" }}
              animate={{ x: 0 }}
              exit={{ x: "100%" }}
              transition={{ type: "spring", damping: 25, stiffness: 200 }}
              className="fixed top-0 right-0 bottom-0 w-80 bg-neutral-900 border-l border-neutral-700 z-50 md:hidden"
            >
              <div className="flex flex-col h-full">
                {/* Header */}
                <div className="flex items-center justify-between p-4 border-b border-neutral-700">
                  <h2 className="text-lg font-semibold text-white">Menu</h2>
                  <Button
                    isIconOnly
                    variant="ghost"
                    onPress={toggleMobileMenu}
                    className="text-neutral-400 hover:text-white hover:bg-neutral-800"
                  >
                    <X size={20} />
                  </Button>
                </div>

                {/* Navigation Items */}
                <div className="flex-1 py-4 flex flex-col">
                  <nav className="space-y-2 px-4">
                    {menuItems.map((item) => {
                      const IconComponent = item.icon;
                      if (item.external) {
                        return (
                          <a
                            key={item.path}
                            href={item.path}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={toggleMobileMenu}
                            className="flex items-center gap-3 px-4 py-3 rounded-lg transition-all duration-200 text-neutral-300 hover:bg-neutral-800/60 hover:text-white"
                          >
                            <IconComponent size={20} />
                            <span className="font-medium">{item.label}</span>
                          </a>
                        );
                      }
                      return (
                        <Link
                          key={item.path}
                          to={item.path}
                          onClick={toggleMobileMenu}
                          className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-all duration-200 ${
                            isActive(item.path)
                              ? "bg-green-500/15 text-green-400 border border-green-500/25 shadow-lg shadow-green-500/5"
                              : "text-neutral-300 hover:bg-neutral-800/60 hover:text-white"
                          }`}
                        >
                          <IconComponent size={20} />
                          <span className="font-medium">{item.label}</span>
                        </Link>
                      );
                    })}
                  </nav>
                </div>

                {/* Footer */}
                {address && (
                  <div className="p-4 border-t border-neutral-700">
                    <div className="flex items-center gap-3 p-3 bg-neutral-800 rounded-lg border border-neutral-700/50">
                      {avatarUrl && (
                        <img
                          src={avatarUrl}
                          alt="Avatar"
                          className="w-10 h-10 rounded-full"
                        />
                      )}
                      <div>
                        <p className="text-sm font-medium text-white">
                          Connected
                        </p>
                        <p className="text-xs text-neutral-400">
                          {address.slice(0, 10)}...{address.slice(-6)}
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Desktop Header */}
      <header className="hidden md:block relative z-20 px-6 py-4 flex justify-end items-center">
        <ConnectModal />
      </header>
    </>
  );
};
